import {
  createContext,
  useContext,
  useEffect,
  ReactNode,
  useState,
  useCallback,
  useMemo,
} from "react";
import { NewUserData, REGISTRATION_STEPS, RegistrationStepType } from "..";
import authService from "@/services/authService";
import { ROLES } from "../interfaces/RolesEnum";
import socialAuthService from "@/services/socialAuthService";
import { User, VisitRegistration } from "../interfaces/User";
import ordersService from "@/services/ordersService";
import useMetaPixel from "@/custom_hooks/useMetaPixel";
import { Order } from "../interfaces/Order";

const AuthContext = createContext<
  | {
      user?: User | null | undefined; //NULL if load attempted
      productToRegisterFor?: VisitRegistration | null;
      registrationStep?: RegistrationStepType;
      login: (
        email: string,
        password: string,
        keepUserLoggedIn: boolean
      ) => Promise<void>;
      logout: () => Promise<void>;
      signUp: (user: NewUserData) => Promise<void>;
      markRegistrationFormSubmit: () => Promise<void>;
      goToNextProductRegistration: () => Promise<void>;
      loadUser: () => Promise<void>;
      order?: Order;
      setOrder: (order: Order) => void;
      authenticateWithGoogle: (code: string) => Promise<void>;
    }
  | undefined
>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const metaPixel = useMetaPixel();
  const [user, setUser] = useState<User | null | undefined>(
    authService.getAccessToken() ? undefined : null,
  );
  const [order, setOrder] = useState<Order | undefined>(); //used to map info for registration of new user

  /** Same-render as `user` — avoids a frame where `user` is set but effect has not run yet (SignUp would redirect). */
  const productToRegisterFor = useMemo((): VisitRegistration | null => {
    if (!user?.visit_registrations?.length) return null;
    const found = user.visit_registrations.find((x) => {
      const step = x.order_item?.current_step;
      return !!step && step !== REGISTRATION_STEPS.DONE;
    });
    return found ?? null;
  }, [user]);

  useEffect(() => {
    loadUser();
  }, []);

  const goToNextProductRegistration = useCallback(async () => {
    const pending =
      user?.visit_registrations?.find((x) => {
        const step = x.order_item?.current_step;
        return !!step && step !== REGISTRATION_STEPS.DONE;
      }) ?? null;

    if (!pending) {
      throw new Error(
        "Tried to mark next product reg step, but no pending visit registration",
      );
    }

    const { currentStep } = await ordersService.nextRegistrationStep(
      pending.order_item.id,
    );
    if (currentStep === REGISTRATION_STEPS.DONE) await loadUser();
    else {
      setUser((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          visit_registrations: prev.visit_registrations.map((vr) =>
            vr.id === pending.id
              ? {
                  ...vr,
                  order_item: {
                    ...vr.order_item,
                    current_step: currentStep,
                  },
                }
              : vr,
          ),
        };
      });
    }
  }, [user]);

  const loadUser = async () => {
    const accessToken = authService.getAccessToken();

    if (!accessToken) {
      setUser(null);
      return;
    }

    try {
      const resp = await authService.getUser();
      setUser({
        ...resp.data.data,
        isAdmin: resp.data.data.role === ROLES.SUPER_ADMIN,
      });
    } catch (err: any) {
      setUser(null);
      if (err.status !== 401) {
        console.error("UNAUTHORIZED");
        refreshToken();
      } else {
        authService.clearAccessToken();
        console.error(err);
        console.dir(err);
        throw err;
      }
    }
  };

  const refreshToken = async () => {
    authService.clearAccessToken();

    // try{
    //     await authService.refreshToken();
    // 	await loadUser();
    // }catch(err: any){
    //    	authService.clearAccessToken();
    // 	console.error(err);
    // 	console.dir(err);
    // 	throw err;
    // }
  };

  const signUp = async (registrationData: NewUserData) => {
    try {
      await authService.register(registrationData);
      await loadUser();
    } catch (err: any) {
      throw err.status === 422
        ? new Error(err.response?.data?.message || "Klaida registruojantis")
        : err; //UNHANDLED
    }
  };

  const login = async (
    email: string,
    password: string,
    keepUserLoggedIn: boolean,
  ) => {
    try {
      await authService.login(email!, password!, keepUserLoggedIn);
      await loadUser();
    } catch (err: any) {
      console.dir(err);
      throw err.status === 422
        ? new Error(err.response?.data?.message || "Nepavyko prisijungti")
        : err; //UNHANDLED
    }
  };

  const authenticateWithGoogle = async (code: string) => {
    try {
      await socialAuthService.authenticateWithGoogle(code);
      await loadUser();
    } catch (err: any) {
      throw err.status === 400
        ? new Error(
            err.response?.data?.message || "Nepavyko prisijungti su Google",
          )
        : err; //UNHANDLED
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };
  const markRegistrationFormSubmit = async () => {
    try {
      await metaPixel.trackQuizCompletion("gyvensena");

      await new Promise((resolve) => setTimeout(resolve, 3000));

      const resp = await authService.markQuestionnaireSubmit();
      if (!user) return;

      const updatedUser = { ...user, ...resp.data.data };
      setUser(updatedUser);
    } catch (err: any) {
      console.error("KLAIDA MARKING REG FORM SUBMIT");
      console.error(err);

      console.dir(err);
    }
  };
  return (
    <AuthContext.Provider
      value={{
        loadUser,
        signUp,
        login,
        user,
        logout,
        productToRegisterFor,
        markRegistrationFormSubmit,
        goToNextProductRegistration,
        order,
        setOrder,
        authenticateWithGoogle,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
